(() => {
  const qs = (sel, ctx = document) => ctx.querySelector(sel);
  const qsa = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const moneyFormat = (cents) => {
    try {
      return (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      });
    } catch (e) {
      return (cents / 100).toFixed(2);
    }
  };

  const lockBody = () => {
    const anyOpen =
      qs('[data-cart-drawer]')?.classList.contains('is-open') ||
      qs('[data-search-drawer]')?.classList.contains('is-open') ||
      qs('[data-mobile-drawer]')?.getAttribute('aria-hidden') === 'false' ||
      qs('[data-size-guide]')?.classList.contains('is-open') ||
      qs('[data-stillness]')?.classList.contains('is-visible');
    document.body.classList.toggle('overflow-hidden', Boolean(anyOpen));
  };

  /* ----------------------------------------------------------------------
     Header scroll + mobile nav
     ---------------------------------------------------------------------- */
  const header = qs('[data-header]');
  const navDrawer = qs('[data-mobile-drawer]');

  const setNavDrawer = (open) => {
    if (!navDrawer) return;
    navDrawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    lockBody();
  };

  qsa('[data-drawer-open]').forEach((el) => el.addEventListener('click', () => setNavDrawer(true)));
  qsa('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => setNavDrawer(false)));

  if (header) {
    let lastY = window.scrollY;
    window.addEventListener(
      'scroll',
      () => {
        const y = window.scrollY;
        header.classList.toggle('is-compressed', y > 24);
        header.classList.toggle('is-hidden', y > lastY && y > 140);
        lastY = y;
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------------------
     Cart drawer — atelier passage
     ---------------------------------------------------------------------- */
  const cartDrawer = qs('[data-cart-drawer]');
  const cartBody = qs('[data-cart-body]');
  const cartCountTargets = qsa('[data-cart-count]');
  const FREE_SHIPPING_THRESHOLD = 15000;
  const SHIP_NUDGE_MAX = 6000;
  const HOLD_KEY = '174963_soft_hold_until';
  const HOLD_DONE_KEY = '174963_soft_hold_done';
  let holdTimerId = null;
  let lastCartTotal = 0;
  const variantStock = new Map();

  const cartI18n = {
    empty: cartDrawer?.dataset.i18nEmpty || 'Your bag is empty',
    continue: cartDrawer?.dataset.i18nContinue || 'Continue shopping',
    continueDrop: cartDrawer?.dataset.i18nContinueDrop || 'View Drop #33',
    remove: cartDrawer?.dataset.i18nRemove || 'Remove',
    checkout: cartDrawer?.dataset.i18nCheckout || 'Continue to payment',
    checkoutOne: cartDrawer?.dataset.i18nCheckoutOne || 'Confirm __TITLE__',
    shipAway: cartDrawer?.dataset.i18nShipAway || '__AMOUNT__ to complimentary shipping',
    shipOk: cartDrawer?.dataset.i18nShipOk || 'Complimentary shipping unlocked',
    holdEnded: cartDrawer?.dataset.i18nHoldEnded || 'Take your time. Editions sell by count, not by this timer.',
    qtyMax: cartDrawer?.dataset.i18nQtyMax || 'Edition limit reached for this size',
  };

  const openCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartDrawer.setAttribute('aria-hidden', 'false');
    lockBody();
    ensureSoftHold();
  };

  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartDrawer.setAttribute('aria-hidden', 'true');
    lockBody();
  };

  const updateCartCount = (count) => {
    cartCountTargets.forEach((el) => {
      el.textContent = String(count);
    });
  };

  const ensureSoftHold = () => {
    const wrap = qs('[data-hold-wrap]');
    const timeEl = qs('[data-hold-time]');
    if (!wrap || !timeEl) return;
    if (sessionStorage.getItem(HOLD_DONE_KEY) === '1') {
      wrap.hidden = false;
      timeEl.textContent = '';
      const label = qs('.cart-drawer__hold-label', wrap);
      if (label) label.textContent = cartI18n.holdEnded;
      return;
    }
    const minutes = Number(cartDrawer?.dataset.holdMinutes || 12);
    const now = Date.now();
    let until = Number(sessionStorage.getItem(HOLD_KEY) || 0);
    if (!until || until < now) {
      until = now + minutes * 60 * 1000;
      sessionStorage.setItem(HOLD_KEY, String(until));
    }
    tickSoftHold();
    if (holdTimerId) clearInterval(holdTimerId);
    holdTimerId = setInterval(tickSoftHold, 1000);
  };

  const tickSoftHold = () => {
    const wrap = qs('[data-hold-wrap]');
    const timeEl = qs('[data-hold-time]');
    if (!wrap || !timeEl) return;
    if (sessionStorage.getItem(HOLD_DONE_KEY) === '1') return;
    const until = Number(sessionStorage.getItem(HOLD_KEY) || 0);
    const remaining = Math.max(0, until - Date.now());
    if (remaining <= 0) {
      sessionStorage.setItem(HOLD_DONE_KEY, '1');
      sessionStorage.removeItem(HOLD_KEY);
      if (holdTimerId) clearInterval(holdTimerId);
      wrap.hidden = false;
      timeEl.textContent = '';
      const label = qs('.cart-drawer__hold-label', wrap);
      if (label) label.textContent = cartI18n.holdEnded;
      return;
    }
    wrap.hidden = false;
    const mins = Math.floor(remaining / 60000);
    const secs = Math.floor((remaining % 60000) / 1000);
    timeEl.textContent = `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const renderShippingBar = (cart) => {
    const wraps = qsa('[data-shipping-bar]');
    if (!wraps.length) return;
    const total = cart.total_price || 0;
    wraps.forEach((wrap) => {
      if (!cart.item_count && !wrap.hasAttribute('data-page-shipping')) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      const remaining = Math.max(FREE_SHIPPING_THRESHOLD - total, 0);
      const progress = Math.min((total / FREE_SHIPPING_THRESHOLD) * 100, 100);
      const fill = qs('[data-shipping-fill]', wrap);
      const text = qs('[data-shipping-text]', wrap);
      if (fill) fill.style.width = `${progress}%`;
      if (text) {
        text.textContent =
          remaining > 0
            ? cartI18n.shipAway.replace('__AMOUNT__', moneyFormat(remaining))
            : cartI18n.shipOk;
      }
      const upsell = qs('[data-ship-upsell]', wrap);
      if (upsell) {
        const show = remaining > 0 && remaining <= SHIP_NUDGE_MAX;
        upsell.hidden = !show;
        if (show && !upsell.dataset.ready) {
          const nudge = upsell.dataset.i18nNudge || 'You’re close. One more piece unlocks free shipping.';
          const cta = upsell.dataset.i18nNudgeCta || 'View Drop #33';
          const url = upsell.dataset.dropUrl || '/#this-drop';
          upsell.innerHTML = `${nudge} <a href="${url}">${cta}</a>`;
          upsell.dataset.ready = '1';
        }
      }
    });
  };

  const updateShipHint = (cartTotal = lastCartTotal) => {
    const hint = qs('[data-ship-hint]');
    if (!hint) return;
    const price = Number(hint.dataset.variantPrice || 0);
    const projected = cartTotal + price;
    const remaining = Math.max(FREE_SHIPPING_THRESHOLD - projected, 0);
    if (cartTotal >= FREE_SHIPPING_THRESHOLD) {
      hint.hidden = true;
      hint.textContent = '';
      return;
    }
    if (remaining === 0) {
      hint.hidden = false;
      hint.textContent = hint.dataset.i18nShipOk || 'Free shipping included';
      hint.classList.remove('is-close');
      return;
    }
    if (remaining <= SHIP_NUDGE_MAX) {
      hint.hidden = false;
      hint.textContent = (hint.dataset.i18nShipAway || '__AMOUNT__ to free shipping').replace(
        '__AMOUNT__',
        moneyFormat(remaining)
      );
      hint.classList.add('is-close');
      return;
    }
    hint.hidden = true;
    hint.textContent = '';
    hint.classList.remove('is-close');
  };

  const renderCeremony = (cart) => {
    const ceremony = qs('[data-first-ceremony]');
    if (!ceremony) return;
    const first = cartDrawer?.dataset.firstOrder === 'true';
    ceremony.hidden = !(first && cart.item_count > 0);
  };

  const renderCheckoutLabel = (cart) => {
    const label = qs('[data-checkout-label]');
    if (!label) return;
    if (cart.items?.length === 1) {
      label.textContent = cartI18n.checkoutOne.replace('__TITLE__', cart.items[0].product_title);
    } else {
      label.textContent = cartI18n.checkout;
    }
  };

  const essenceFromItem = (item) => {
    const raw = (item.product_description || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    if (!raw) return '';
    return raw.length > 110 ? `${raw.slice(0, 110).trim()}…` : raw;
  };

  const renderCart = (cart) => {
    if (!cartBody) return;
    updateCartCount(cart.item_count || 0);
    renderShippingBar(cart);
    renderCeremony(cart);
    renderCheckoutLabel(cart);

    if (!cart.items || cart.items.length === 0) {
      lastCartTotal = 0;
      updateShipHint(0);
      const dropUrl = cartDrawer?.dataset.dropUrl || '/#this-drop';
      cartBody.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">${cartI18n.empty}</p>
          <a class="button button--outline" href="${dropUrl}" data-empty-drop>${cartI18n.continueDrop}</a>
        </div>`;
      qs('[data-empty-drop]', cartBody)?.addEventListener('click', (event) => {
        event.preventDefault();
        closeCart();
        window.location.href = dropUrl;
      });
      const footer = qs('[data-cart-footer]');
      if (footer) footer.hidden = true;
      const hold = qs('[data-hold-wrap]');
      if (hold) hold.hidden = true;
      return;
    }

    lastCartTotal = cart.total_price || 0;
    updateShipHint(lastCartTotal);

    const footer = qs('[data-cart-footer]');
    if (footer) footer.hidden = false;
    ensureSoftHold();

    cartBody.innerHTML = `<div class="cart-drawer__items">${cart.items
      .map((item) => {
        const image = item.image
          ? `<img src="${item.image}" alt="${item.product_title.replace(/"/g, '&quot;')}" width="120" height="160" loading="lazy">`
          : '';
        const variant =
          item.variant_title && item.variant_title !== 'Default Title'
            ? `<p class="cart-item__meta">${item.variant_title}</p>`
            : '';
        const essence = essenceFromItem(item);
        const essenceHtml = essence ? `<p class="cart-item__essence">${essence}</p>` : '';
        return `
          <article class="cart-item" data-key="${item.key}" data-variant-id="${item.variant_id}" data-max="${
            variantStock.has(item.variant_id) ? variantStock.get(item.variant_id) : ''
          }">
            <div class="cart-item__media">${image}</div>
            <div class="cart-item__info">
              <h3 class="cart-item__title">${item.product_title}</h3>
              ${variant}
              ${essenceHtml}
              <p class="cart-item__meta">${moneyFormat(item.final_line_price)}</p>
              <div class="cart-item__row">
                <div class="cart-item__qty">
                  <button type="button" data-qty-change="-1" aria-label="Decrease quantity">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" data-qty-change="1" aria-label="Increase quantity"${
                    variantStock.has(item.variant_id) && item.quantity >= variantStock.get(item.variant_id)
                      ? ' disabled'
                      : ''
                  }>+</button>
                </div>
                <button type="button" class="cart-item__remove" data-remove-item>${cartI18n.remove}</button>
              </div>
            </div>
          </article>`;
      })
      .join('')}</div>`;

    const subtotal = qs('[data-cart-subtotal]');
    if (subtotal) subtotal.textContent = moneyFormat(cart.total_price);

    qsa('.cart-item', cartBody).forEach((row) => {
      const key = row.getAttribute('data-key');
      qsa('[data-qty-change]', row).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const delta = Number(btn.getAttribute('data-qty-change'));
          const qtyEl = qs('.cart-item__qty span', row);
          const next = Math.max(0, Number(qtyEl.textContent) + delta);
          const max = Number(row.dataset.max || 0);
          if (max > 0 && next > max) {
            setCartPageStatus(cartI18n.qtyMax);
            return;
          }
          await changeCart(key, next);
        });
      });
      qs('[data-remove-item]', row)?.addEventListener('click', async () => {
        await changeCart(key, 0);
      });
    });
  };

  const fetchCart = async () => {
    const res = await fetch('/cart.js');
    const cart = await res.json();
    renderCart(cart);
    return cart;
  };

  const setCartPageStatus = (message) => {
    const el = qs('[data-cart-page-status]');
    if (!el) return;
    if (!message) {
      el.hidden = true;
      el.textContent = '';
      return;
    }
    el.hidden = false;
    el.textContent = message;
  };

  const syncCartPage = (cart) => {
    const page = qs('[data-cart-page]');
    if (!page) return;
    if (!cart.item_count) {
      window.location.reload();
      return;
    }
    const subtotal = qs('[data-cart-page-subtotal]');
    if (subtotal) subtotal.textContent = moneyFormat(cart.total_price);
    renderShippingBar(cart);
    const keys = new Set((cart.items || []).map((item) => item.key));
    qsa('.cart-item', page).forEach((row) => {
      if (!keys.has(row.getAttribute('data-key'))) row.remove();
    });
    cart.items.forEach((item) => {
      const row = page.querySelector(`[data-key="${item.key}"]`);
      if (!row) {
        window.location.reload();
        return;
      }
      const qtyEl = qs('[data-qty]', row);
      if (qtyEl) qtyEl.textContent = String(item.quantity);
      const priceEl = qs('[data-line-price]', row);
      if (priceEl) priceEl.textContent = moneyFormat(item.final_line_price);
      const max = Number(row.dataset.max || 0);
      const plus = qs('[data-qty-change="1"]', row);
      if (plus && max) plus.disabled = item.quantity >= max;
    });
  };

  const changeCart = async (key, quantity) => {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
    let data = null;
    try {
      data = await res.json();
    } catch (e) {
      data = null;
    }
    if (!res.ok) {
      const message = data?.description || data?.message || 'Could not update quantity';
      setCartPageStatus(message);
      const cart = await fetchCart();
      return cart;
    }
    setCartPageStatus('');
    renderCart(data);
    syncCartPage(data);
    return data;
  };

  const saveGiftNote = async () => {
    const note = qs('[data-gift-note]')?.value || '';
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ note }),
    });
  };

  const runStillnessCheckout = async (event) => {
    event?.preventDefault?.();
    await saveGiftNote();
    const overlay = qs('[data-stillness]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    if (!overlay || reduced || isMobile) {
      window.location.href = '/checkout';
      return;
    }
    overlay.classList.add('is-visible');
    overlay.setAttribute('aria-hidden', 'false');
    setTimeout(() => {
      window.location.href = '/checkout';
    }, 420);
  };

  qsa('[data-cart-open]').forEach((el) => {
    el.addEventListener('click', async (event) => {
      event.preventDefault();
      await fetchCart();
      openCart();
    });
  });
  qsa('[data-cart-close]').forEach((el) => el.addEventListener('click', closeCart));
  qs('[data-cart-overlay]')?.addEventListener('click', closeCart);
  qs('[data-gift-note]')?.addEventListener('change', saveGiftNote);
  qsa('[data-stillness-checkout]').forEach((el) => {
    el.addEventListener('click', runStillnessCheckout);
  });

  /* ----------------------------------------------------------------------
     Search drawer + predictive search
     ---------------------------------------------------------------------- */
  const searchDrawer = qs('[data-search-drawer]');
  const searchInput = qs('[data-search-input]');
  const searchResults = qs('[data-search-results]');
  let searchTimer;

  const openSearch = () => {
    if (!searchDrawer) return;
    searchDrawer.classList.add('is-open');
    searchDrawer.setAttribute('aria-hidden', 'false');
    lockBody();
    setTimeout(() => searchInput?.focus(), 80);
  };

  const closeSearch = () => {
    if (!searchDrawer) return;
    searchDrawer.classList.remove('is-open');
    searchDrawer.setAttribute('aria-hidden', 'true');
    lockBody();
  };

  qsa('[data-search-open]').forEach((el) => {
    el.addEventListener('click', (event) => {
      event.preventDefault();
      openSearch();
    });
  });
  qsa('[data-search-close]').forEach((el) => el.addEventListener('click', closeSearch));

  const renderSearchResults = (items) => {
    if (!searchResults) return;
    if (!items.length) {
      searchResults.innerHTML = `<p class="search-drawer__empty">No results found</p>`;
      return;
    }
    searchResults.innerHTML = items
      .map((item) => {
        const img = item.featured_image?.url
          ? `<img src="${item.featured_image.url}" alt="" loading="lazy" width="90" height="120">`
          : '';
        return `
          <a class="search-result" href="${item.url}">
            <div class="search-result__media">${img}</div>
            <div>
              <p class="search-result__title">${item.title}</p>
              <p class="search-result__price">${item.price ? moneyFormat(item.price) : ''}</p>
            </div>
          </a>`;
      })
      .join('');
  };

  searchInput?.addEventListener('input', () => {
    clearTimeout(searchTimer);
    const q = searchInput.value.trim();
    if (q.length < 2) {
      searchResults.innerHTML = `<p class="search-drawer__hint">Start typing to search</p>`;
      return;
    }
    searchTimer = setTimeout(async () => {
      try {
        const res = await fetch(
          `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8`
        );
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        renderSearchResults(
          products.map((p) => ({
            title: p.title,
            url: p.url,
            price: Math.round(parseFloat(p.price) * 100) || 0,
            featured_image: p.image ? { url: p.image } : null,
          }))
        );
      } catch (e) {
        searchResults.innerHTML = `<p class="search-drawer__empty">Search unavailable</p>`;
      }
    }, 220);
  });

  /* ----------------------------------------------------------------------
     Product variants + sticky ATC + size guide
     ---------------------------------------------------------------------- */
  const productJsonEl = qs('[data-product-json]');
  const productForm = qs('[data-product-form], .product-form');

  if (productJsonEl && productForm) {
    const product = JSON.parse(productJsonEl.textContent);
    product.variants?.forEach((variant) => {
      if (typeof variant.inventory_quantity === 'number') {
        variantStock.set(variant.id, variant.inventory_quantity);
      }
    });
    const variantInput = qs('[data-variant-id]', productForm);
    const addButtons = qsa('[data-add-to-cart], [data-sticky-atc-button]');
    const priceEls = qsa('[data-product-price]');
    const sticky = qs('[data-sticky-atc]');
    const mainAtc = qs('[data-main-atc]');
    const statusEl = qs('[data-form-status]');
    const shipHint = qs('[data-ship-hint]');
    const stickySize = qs('[data-sticky-size]');
    const stickyEdition = qs('[data-sticky-edition]');
    const stickyBtn = qs('[data-sticky-atc-button]');
    const i18nAdd = stickyBtn?.dataset.i18nAdd || 'Add to cart';
    const i18nSold = stickyBtn?.dataset.i18nSold || 'Sold out';
    const i18nSelect = stickyBtn?.dataset.i18nSelect || 'Select a size';
    const i18nUnavailable = 'Size unavailable. Choose another.';
    const i18nError = 'Couldn’t add. That size may just have sold.';

    const setStatus = (message) => {
      if (!statusEl) return;
      if (!message) {
        statusEl.hidden = true;
        statusEl.textContent = '';
        return;
      }
      statusEl.hidden = false;
      statusEl.textContent = message;
    };

    const getSelectedOptions = () =>
      qsa('[data-option-input]:checked', productForm).map((input) => input.value);

    const findVariant = () => {
      const selected = getSelectedOptions();
      if (!selected.length) return product.variants[0];
      return product.variants.find((variant) =>
        selected.every((value, index) => variant[`option${index + 1}`] === value)
      );
    };

    const selectedSizeEl = qs('[data-selected-size]');

    const syncVariant = () => {
      const selected = getSelectedOptions();
      if (selectedSizeEl && selected[0]) {
        selectedSizeEl.textContent = `· ${selected[0]}`;
      }
      if (stickySize) {
        stickySize.textContent = selected[0] ? selected[0] : '';
      }

      const variant = findVariant();
      if (!variant) {
        addButtons.forEach((btn) => {
          btn.disabled = true;
          btn.textContent = 'Unavailable';
        });
        setStatus(i18nUnavailable);
        return;
      }
      if (variantInput) variantInput.value = variant.id;
      if (shipHint) shipHint.dataset.variantPrice = String(variant.price || 0);
      updateShipHint(lastCartTotal);
      priceEls.forEach((el) => {
        el.textContent = moneyFormat(variant.price);
      });
      addButtons.forEach((btn) => {
        btn.disabled = !variant.available;
        btn.textContent = variant.available ? i18nAdd : i18nSold;
      });
      if (!variant.available) {
        setStatus(i18nUnavailable);
      } else {
        setStatus('');
      }
      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      window.history.replaceState({}, '', url.pathname + url.search + url.hash);
    };

    qsa('[data-option-input]', productForm).forEach((input) => {
      input.addEventListener('change', syncVariant);
    });

    productForm.addEventListener('submit', async (event) => {
      event.preventDefault();
      const variant = findVariant();
      if (!variant) {
        setStatus(i18nSelect);
        qs('.variant-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (!variant.available) {
        setStatus(i18nUnavailable);
        return;
      }
      try {
        setStatus('');
        await window.Theme174963.addToCart(variant.id, 1);
      } catch (err) {
        setStatus(i18nError);
      }
    });

    stickyBtn?.addEventListener('click', () => {
      const variant = findVariant();
      if (!variant?.available) {
        setStatus(variant ? i18nUnavailable : i18nSelect);
        qs('.variant-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      productForm.requestSubmit();
    });

    if (sticky && mainAtc && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          sticky.classList.toggle('is-visible', !entry.isIntersecting);
          sticky.setAttribute('aria-hidden', entry.isIntersecting ? 'true' : 'false');
        },
        { threshold: 0 }
      );
      observer.observe(mainAtc);
    }

    syncVariant();
  }

  /* Size guide app */
  const sizeGuide = qs('[data-size-guide]');
  const setSizeTab = (tab) => {
    if (!sizeGuide || !tab) return;
    qsa('[data-size-tab]', sizeGuide).forEach((btn) => {
      const active = btn.getAttribute('data-size-tab') === tab;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    qsa('[data-size-pane]', sizeGuide).forEach((pane) => {
      const active = pane.getAttribute('data-size-pane') === tab;
      pane.classList.toggle('is-active', active);
      if (active) pane.removeAttribute('hidden');
      else pane.setAttribute('hidden', '');
    });
  };

  const openSizeGuide = (event) => {
    if (!sizeGuide) return;
    event?.preventDefault?.();
    const tab = event?.currentTarget?.getAttribute?.('data-size-tab');
    if (tab) setSizeTab(tab);
    sizeGuide.classList.add('is-open');
    sizeGuide.setAttribute('aria-hidden', 'false');
    lockBody();
  };
  const closeSizeGuide = () => {
    if (!sizeGuide) return;
    sizeGuide.classList.remove('is-open');
    sizeGuide.setAttribute('aria-hidden', 'true');
    lockBody();
  };
  qsa('[data-size-guide-open]').forEach((el) => el.addEventListener('click', openSizeGuide));
  qsa('[data-size-guide-close]').forEach((el) => el.addEventListener('click', closeSizeGuide));
  qsa('[data-size-tab]').forEach((el) => {
    el.addEventListener('click', () => setSizeTab(el.getAttribute('data-size-tab')));
  });

  /* Escape closes all */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      closeCart();
      closeSearch();
      closeSizeGuide();
      setNavDrawer(false);
    }
  });

  /* Reveal on scroll */
  if ('IntersectionObserver' in window) {
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            revealObs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    qsa('[data-reveal]').forEach((el) => revealObs.observe(el));
  }

  /* Sunday 18:00 ritual countdown (Europe/Berlin) */
  const dropRoot = qs('[data-drop-countdown]');
  if (dropRoot) {
    const DROP_HOUR = Number(dropRoot.dataset.dropHour || 18);
    const TZ = 'Europe/Berlin';
    const pad = (n) => String(Math.max(0, n)).padStart(2, '0');
    const partsEl = {
      days: qs('[data-days]', dropRoot),
      hours: qs('[data-hours]', dropRoot),
      minutes: qs('[data-minutes]', dropRoot),
      seconds: qs('[data-seconds]', dropRoot),
    };

    const berlinParts = (date) => {
      const map = {};
      new Intl.DateTimeFormat('en-GB', {
        timeZone: TZ,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        weekday: 'short',
        hour12: false,
      })
        .formatToParts(date)
        .forEach((p) => {
          if (p.type !== 'literal') map[p.type] = p.value;
        });
      return map;
    };

    /** Convert a Berlin wall-clock moment to a real Date (handles DST). */
    const berlinWallToDate = (year, month, day, hour, minute = 0, second = 0) => {
      const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
      const shown = berlinParts(new Date(utcGuess));
      const asIfUtc = Date.UTC(
        Number(shown.year),
        Number(shown.month) - 1,
        Number(shown.day),
        Number(shown.hour),
        Number(shown.minute),
        Number(shown.second)
      );
      return new Date(utcGuess - (asIfUtc - utcGuess));
    };

    const nextSundayEighteen = () => {
      const now = new Date();
      const bp = berlinParts(now);
      const y = Number(bp.year);
      const m = Number(bp.month);
      const d = Number(bp.day);
      const dowMap = { Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6, Sun: 0 };
      const dow = dowMap[bp.weekday] ?? 0;

      for (let add = 0; add <= 7; add += 1) {
        const probe = new Date(Date.UTC(y, m - 1, d + add, 12, 0, 0));
        const pp = berlinParts(probe);
        if ((dowMap[pp.weekday] ?? -1) !== 0) continue;
        const target = berlinWallToDate(
          Number(pp.year),
          Number(pp.month),
          Number(pp.day),
          DROP_HOUR,
          0,
          0
        );
        if (target.getTime() > now.getTime()) return target;
      }

      // Fallback: next week's Sunday
      const fallbackProbe = new Date(Date.UTC(y, m - 1, d + 7, 12, 0, 0));
      const fp = berlinParts(fallbackProbe);
      return berlinWallToDate(Number(fp.year), Number(fp.month), Number(fp.day), DROP_HOUR, 0, 0);
    };

    const tick = () => {
      const now = new Date();
      const bp = berlinParts(now);
      const hourNow = Number(bp.hour);
      const isSundayLive = bp.weekday === 'Sun' && hourNow >= DROP_HOUR;
      const target = nextSundayEighteen();
      let diff = Math.max(0, target.getTime() - Date.now());
      const days = Math.floor(diff / 86400000);
      diff -= days * 86400000;
      const hours = Math.floor(diff / 3600000);
      diff -= hours * 3600000;
      const minutes = Math.floor(diff / 60000);
      diff -= minutes * 60000;
      const seconds = Math.floor(diff / 1000);
      if (partsEl.days) partsEl.days.textContent = pad(days);
      if (partsEl.hours) partsEl.hours.textContent = pad(hours);
      if (partsEl.minutes) partsEl.minutes.textContent = pad(minutes);
      if (partsEl.seconds) partsEl.seconds.textContent = pad(seconds);

      const soon = !isSundayLive && days === 0 && hours < 6;
      dropRoot.classList.toggle('is-live', isSundayLive);
      dropRoot.classList.toggle('is-soon', soon);
      const label = qs('[data-drop-label]', dropRoot);
      const note = qs('[data-drop-note]', dropRoot);
      const cta = qs('[data-drop-cta]', dropRoot);
      if (isSundayLive) {
        if (label) label.textContent = dropRoot.dataset.i18nLive || 'Drop #33 is live';
        if (note) note.textContent = dropRoot.dataset.i18nLiveNote || 'This week’s archive is open';
        if (cta) cta.textContent = dropRoot.dataset.i18nView || 'View Drop #33';
      } else if (soon) {
        if (label) label.textContent = dropRoot.dataset.i18nSoon || 'Drop opens soon';
        if (note) note.textContent = dropRoot.dataset.i18nNote || 'Every Sunday · 18:00';
        if (cta) cta.textContent = dropRoot.dataset.i18nView || 'View Drop #33';
      } else {
        if (label) label.textContent = dropRoot.dataset.i18nNext || 'Next drop';
        if (note) note.textContent = dropRoot.dataset.i18nNote || 'Every Sunday · 18:00';
        if (cta) cta.textContent = dropRoot.dataset.i18nView || 'View Drop #33';
      }
    };

    tick();
    setInterval(tick, 1000);
  }

  /* Cart page qty / remove */
  const cartPage = qs('[data-cart-page]');
  if (cartPage) {
    ensureSoftHold();
    qsa('.cart-item', cartPage).forEach((row) => {
      const key = row.getAttribute('data-key');
      qsa('[data-qty-change]', row).forEach((btn) => {
        btn.addEventListener('click', async () => {
          const delta = Number(btn.getAttribute('data-qty-change'));
          const qtyEl = qs('[data-qty]', row);
          const next = Math.max(0, Number(qtyEl?.textContent || 0) + delta);
          const max = Number(row.dataset.max || 0);
          if (max > 0 && next > max) {
            setCartPageStatus(cartI18n.qtyMax);
            return;
          }
          await changeCart(key, next);
        });
      });
      qs('[data-remove-item]', row)?.addEventListener('click', async () => {
        await changeCart(key, 0);
      });
    });
  }

  window.Theme174963 = {
    openCart,
    closeCart,
    openSearch,
    closeSearch,
    refreshCart: fetchCart,
    addToCart: async (variantId, quantity = 1) => {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(variantId), quantity }),
      });
      if (!res.ok) {
        let message = 'Unable to add to cart';
        try {
          const err = await res.json();
          if (err?.description) message = err.description;
        } catch (e) {
          /* ignore */
        }
        throw new Error(message);
      }
      const counter = qs('[data-edition-counter]');
      if (counter) {
        const left = Math.max(0, Number(counter.dataset.editionLeft || 0) - quantity);
        const limit = Number(counter.dataset.editionLimit || 10);
        counter.dataset.editionLeft = String(left);
        counter.classList.toggle('pdp__edition--low', left > 0 && left <= 3);
        counter.classList.toggle('pdp__edition--out', left === 0);
        const label = qs('[data-edition-label]', counter);
        if (label) {
          label.textContent =
            left === 0 ? 'Edition complete' : `${left} available`;
        }
        const stickyEdition = qs('[data-sticky-edition]');
        if (stickyEdition) {
          stickyEdition.textContent = left === 0 ? 'Edition complete' : `${left} available`;
        }
      }
      await fetchCart();
      openCart();
    },
  };

  fetchCart().catch(() => {});

  /* Cookie consent */
  const cookieBanner = qs('[data-cookie-banner]');
  const cookieKey = '174963_cookie_consent';
  if (cookieBanner && !localStorage.getItem(cookieKey)) {
    requestAnimationFrame(() => cookieBanner.classList.add('is-visible'));
  }
  qs('[data-cookie-accept]')?.addEventListener('click', () => {
    localStorage.setItem(cookieKey, 'accepted');
    cookieBanner?.classList.remove('is-visible');
  });
  qs('[data-cookie-decline]')?.addEventListener('click', () => {
    localStorage.setItem(cookieKey, 'declined');
    cookieBanner?.classList.remove('is-visible');
  });
})();
